import { lazy } from 'react';

import {
  ApiOutlined,
  DollarCircleOutlined,
  DollarOutlined,
  SettingOutlined,
  ToolOutlined,
  LineChartOutlined,
  UserAddOutlined,
} from '@ant-design/icons';

import AlertIcon from '../components/atoms/icons/AlertIcon';
import AnalyticsIcon from '../components/atoms/icons/AnalyticsIcon';
import AuditIcon from '../components/atoms/icons/AuditIcon';
import ChargerIcon from '../components/atoms/icons/ChargerIcon';
import ChartPieIcon from '../components/atoms/icons/ChartPieIcon';
import DashboardIcon from '../components/atoms/icons/DashboardIcon';
import InteroperabilityIcon from '../components/atoms/icons/InteroperabilityIcon';
import LogIcon from '../components/atoms/icons/LogIcon';
import OcppTagIcon from '../components/atoms/icons/OcppTagIcon';
import OperationsIcon from '../components/atoms/icons/OperationsIcon';
import PushNotificationIcon from '../components/atoms/icons/PushNotificationIcon';
import { StationGroupIcon } from '../components/atoms/icons/StationGroupIcon';
import SupportIcon from '../components/atoms/icons/SupportIcon';
import TicketsIcon from '../components/atoms/icons/TicketsIcon';
import TransactionIcon from '../components/atoms/icons/TransactionIcon';
import UserIcon from '../components/atoms/icons/UserIcon';
import VoucherIcon from '../components/atoms/icons/VoucherIcon';
import AlertsPage from '../components/pages/AlertsPage';
import AuditLogList from '../components/pages/AuditLogList';
import ChangePasswordPage from '../components/pages/authentication/ChangePasswordPage/ChangePasswordPage';
import LoginPage from '../components/pages/authentication/LoginPage/LoginPage';
import RecoverPage from '../components/pages/authentication/RecoveryPage/RecoverPage';
import RegisterPage from '../components/pages/authentication/RegisterPage/RegisterPage';
import ChargingProfileCreatePage from '../components/pages/ChargingProfileCreatePage';
import ChargingProfileEditPage from '../components/pages/ChargingProfileEditPage';
import CreateAccount from '../components/pages/CreateAccount/CreateAccount';
import ProfileCreatePage from '../components/pages/DynamicPricing/ProfileCreatePage';
import ProfileListPage from '../components/pages/DynamicPricing/ProfileListPage';
import SetPricingPage from '../components/pages/DynamicPricing/SetPricingPage';
import FinancialList from '../components/pages/FinancialList';
import Home from '../components/pages/Home';
import InteroperabilityPage from '../components/pages/Interoperability/InteroperabilityPage';
import LogPage from '../components/pages/LogPage/LogPage';
import MonitoringPage from '../components/pages/MonitoringPage/MonitoringPage';
import NotFoundPage from '../components/pages/NotFoundPage';
import OcpiPage from '../components/pages/OcpiPage';
import OcppTagEditPage from '../components/pages/OcppTagEditPage';
import OcppTagListPage from '../components/pages/OcppTagListPage';
import OcppTagsCreatePage from '../components/pages/OcppTagsCreatePage';
import OperationsOCPPPage from '../components/pages/OperationsPage';
import PrivacyPolicyPage from '../components/pages/PrivacyPolicyPage';
import PushNotificationPage from '../components/pages/PushNotificationPage';
import QrCodeStartPage from '../components/pages/QrCodeStartPage/QrCodeStartPage';
import SmartChargingSimulatorPage from '../components/pages/SmartChargingSimulatorPage/SmartChargingSimulatorPage';
import StationCreatePage from '../components/pages/StationCreatePage';
import StationEditPage from '../components/pages/StationEditPage';
import StationGroupCreatePage from '../components/pages/StationGroupCreatePage';
import StationGroupEditPage from '../components/pages/StationGroupEditPage';
import StationGroupListPage from '../components/pages/StationGroupListPage';
import StationsList from '../components/pages/StationListPage';
import SupportPage from '../components/pages/SupportPage';
import TicketsPage from '../components/pages/Tickets';
import TicketsInternalPage from '../components/pages/TicketsInternal';
import TermsOfUsePage from '../components/pages/TermsOfUsePage';
import ThreeDSAuthentication from '../components/pages/ThreeDSAuthentication';
import TransactionList from '../components/pages/TransactionList';
import FirmwareHistoryList from '../components/pages/FirmwareHistoryList';
import MaintenanceList from '../components/pages/MaintenanceList';
import UnknownTagListPage from '../components/pages/UnknownTagListPage';
import UserCreatePage from '../components/pages/UserCreatePage';
import UserEditPage from '../components/pages/UserEditPage';
import UserListPage from '../components/pages/UserListPage';
import VirtualGroupChargeboxes from '../components/pages/VirtualGroup/VirtualGroupChargeboxes';
import VirtualGroupCreatePage from '../components/pages/VirtualGroup/VirtualGroupCreatePage';
import VirtualGroupEditChargeboxes from '../components/pages/VirtualGroup/VirtualGroupEditChargeboxes';
import VirtualGroupEditPage from '../components/pages/VirtualGroup/VirtualGroupEditPage';
import VirtualGroupListPage from '../components/pages/VirtualGroup/VirtualGroupListPage';
import VirtualGroupPage from '../components/pages/VirtualGroup/VirtualGroupPage';
import VoucherCreatePage from '../components/pages/VoucherCreatePage';
import VoucherDetailPage from '../components/pages/VoucherDetailPage';
import VoucherEditPage from '../components/pages/VoucherEditPage';
import VoucherListPage from '../components/pages/VoucherListPage';
import VoucherSelectUsersPage from '../components/pages/VoucherSelectUsersPage';
import CompanyCreatePage from '../components/pages/CompanyCreatePage/CompanyCreatePage';
import CompanyEditPage from '../components/pages/CompanyEditPage/CompanyEditPage';
import AnalyticsPage from '../components/pages/AnalyticsPage/AnalyticsPage';

// Lazy-loaded to keep this screen's chunk out of the main bundle shipped to
// every visitor before login. Originally added to reduce exposure of an
// Airtable token read here (REACT_APP_INDICATORS_DRE_AIRTABLE_TOKEN); that
// token no longer exists — Airtable access now goes through the same-origin
// /airtable/ proxy instead — but the lazy load itself is still worth keeping.
const IndicatorsPage = lazy(() => import('../components/pages/IndicatorsPage'));

export const staticRoutes = [
  {
    title: 'General View',
    path: '/',
    component: <Home />,
    isMenuSidebarItem: true,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: true,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
    menuIcon: (fill: string) => <DashboardIcon fill={fill} />,
  },
  {
    title: 'Firmware History',
    path: '/firmware-history',
    component: <FirmwareHistoryList />,
    isMenuSidebarItem: true,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: true,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
    menuIcon: (fill: string) => <OperationsIcon fill={fill} />,
  },
  {
    title: 'Chargeboxes',
    path: '/stations',
    component: <StationsList />,
    isMenuSidebarItem: true,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: true,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
    menuIcon: (fill: string) => <ChargerIcon fill={fill} />,
  },
  {
    title: 'Chargebox Groups',
    path: '/station_groups',
    component: <StationGroupListPage />,
    isMenuSidebarItem: true,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: true,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
    menuIcon: (fill: string) => <StationGroupIcon size={15} color={fill} />,
  },
  {
    title: 'Interoperability',
    path: '/interoperability',
    component: <InteroperabilityPage />,
    isMenuSidebarItem: true,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: true,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
    menuIcon: (fill: string) => <InteroperabilityIcon fill={fill} />,
  },
  {
    title: 'Users',
    path: '/users',
    component: <UserListPage />,
    isMenuSidebarItem: true,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: true,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
    menuIcon: (fill: string) => <UserIcon fill={fill} />,
  },
  {
    title: 'Ocpp Tags',
    path: '/ocpptags',
    component: <OcppTagListPage />,
    isMenuSidebarItem: true,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: true,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
    menuIcon: (fill: string) => <OcppTagIcon fill={fill} />,
  },
  {
    title: 'Operations',
    path: '/operations',
    component: <OperationsOCPPPage />,
    isMenuSidebarItem: true,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: true,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
    menuIcon: (fill: string) => <OperationsIcon fill={fill} />,
  },
  {
    title: 'Transactions Payment Edit',
    path: '/transactions/:id/payment/edit',
    component: <TransactionList />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
    menuIcon: (fill: string) => <TransactionIcon fill={fill} />,
  },
  {
    title: 'Transactions',
    path: '/transactions',
    component: <TransactionList />,
    isMenuSidebarItem: true,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: true,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
    menuIcon: (fill: string) => <TransactionIcon fill={fill} />,
  },
  {
    title: 'Financial',
    path: '/financial',
    component: <FinancialList />,
    isMenuSidebarItem: true,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: true,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
    menuIcon: (fill: string) => <DollarOutlined color={fill} />,
  },
  {
    title: 'Indicators',
    path: '/indicators',
    component: <IndicatorsPage />,
    isMenuSidebarItem: true,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: true,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
    menuIcon: (fill: string) => <AnalyticsIcon fill={fill} />,
  },
  {
    title: 'Alerts',
    path: '/alerts',
    component: <AlertsPage />,
    isMenuSidebarItem: true,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: true,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
    menuIcon: (fill: string) => <AlertIcon fill={fill} />,
  },
  {
    title: 'Log',
    path: '/log',
    component: <LogPage />,
    isMenuSidebarItem: true,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: true,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
    menuIcon: (fill: string) => <LogIcon fill={fill} />,
  },
  {
    title: 'Audit Log',
    path: '/audit-log',
    component: <AuditLogList />,
    isMenuSidebarItem: true,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: true,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
    menuIcon: (fill: string) => <AuditIcon fill={fill} />,
  },
  {
    title: 'Support',
    path: '/support',
    component: <SupportPage />,
    isMenuSidebarItem: true,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: true,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
    menuIcon: (fill: string) => <SupportIcon fill={fill} />,
    externalLink: true,
  },
  {
    title: 'Tickets',
    path: '/tickets',
    component: <TicketsPage />,
    isMenuSidebarItem: true,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: true,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
    menuIcon: (fill: string) => <TicketsIcon fill={fill} />,
  },
  {
    title: 'Tickets (Internal)',
    path: '/tickets-internal',
    component: <TicketsInternalPage />,
    isMenuSidebarItem: true,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: true,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
    menuIcon: (fill: string) => <TicketsIcon fill={fill} />,
  },
  {
    title: 'LOGIN PAGE',
    path: '/auth/login',
    component: <LoginPage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: false,
    isAuthenticationPage: true,
  },
  {
    title: 'REGISTER PAGE',
    path: '/auth/register',
    component: <RegisterPage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: false,
    isAuthenticationPage: true,
  },
  {
    title: 'RECOVER PAGE',
    path: '/auth/recover',
    component: <RecoverPage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: false,
    isAuthenticationPage: true,
  },
  {
    title: 'CHANGE PASSWORD PAGE',
    path: '/auth/recover/change',
    component: <ChangePasswordPage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: false,
    isAuthenticationPage: true,
  },
  {
    title: 'CREATE ACCOUNT',
    path: '/auth/createaccount',
    component: <CreateAccount />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: false,
    isAuthenticationPage: true,
  },
  {
    title: 'USER PAGE',
    path: '/user/:id',
    component: <UserEditPage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: false,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'CREATE STATION',
    path: '/stations/create',
    component: <StationCreatePage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'CREATE STATION GROUP',
    path: '/station_groups/create',
    component: <StationGroupCreatePage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'CREATE USER',
    path: '/users/create',
    component: <UserCreatePage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'CREATE VEHICLE',
    path: '/vehicle/create',
    component: <UserCreatePage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'EDIT VEHICLE',
    path: '/vehicle/:id/edit',
    component: <UserEditPage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'CREATE COMPANY',
    path: '/company/create',
    component: <CompanyCreatePage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'EDIT COMPANY',
    path: '/company/:id/edit',
    component: <CompanyEditPage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'EDIT STATION',
    path: '/stations/edit/:id',
    component: <StationEditPage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: false,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'DELETE STATION',
    path: '/stations/delete/:id',
    component: <></>,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: false,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'EDIT STATION GROUP',
    path: '/station_groups/edit/:id',
    component: <StationGroupEditPage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: false,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'OPERATION OCPP 1.5',
    path: '/operations/v15',
    component: <></>,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'OPERATION OCPP 1.6',
    path: '/operations/v16',
    component: <></>,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'OPERATION TASKS',
    path: '/operations/tasks',
    component: <></>,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'OPERATION CHANGE AVAILABILITY',
    path: '/operations/change_availability',
    component: <></>,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'OPERATION START',
    path: '/operations/remote_start',
    component: <></>,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'OPERATION STOP',
    path: '/operations/remote_stop',
    component: <></>,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'OPERATION RESET',
    path: '/operations/remote_reset',
    component: <></>,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'OPERATION GET CONFIGURATION',
    path: '/operations/get_configuration',
    component: <></>,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'OPERATION CHANGE CONFIGURATION',
    path: '/operations/change_configuration',
    component: <></>,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'OPERATION UNLOCK CONNECTOR',
    path: '/operations/unlock_connector',
    component: <></>,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'OPERATION UPDATE FIRMWARE',
    path: '/operations/update_firmware',
    component: <></>,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'OPERATION DATA TRANSFER',
    path: '/operations/data_transfer',
    component: <></>,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'OPERATION TRIGGER MESSAGE',
    path: '/operations/trigger_message',
    component: <></>,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'OPERATION CHARGING PROFILE',
    path: '/operations/charging_profile',
    component: <></>,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'OPERATION SET CHARGING PROFILE',
    path: '/operations/set_charging_profile',
    component: <></>,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'OPERATION CLEAR CHARGING PROFILE',
    path: '/operations/clear_charging_profile',
    component: <></>,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'OPERATION SEND LOCAL LIST',
    path: '/operations/send_local_list',
    component: <></>,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'OPERATION GET COMPOSITE SCHEDULE',
    path: '/operations/get_composite_schedule',
    component: <></>,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'OPERATION RESERVE NOW',
    path: '/operations/reserve_now',
    component: <></>,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'OPERATION GET DIAGNOSTICS',
    path: '/operations/get_diagnostics',
    component: <></>,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'OPERATION CLEAR CACHE',
    path: '/operations/clear_cache',
    component: <></>,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'ADMIN',
    path: '/admin',
    component: <Home />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'CONFIG',
    path: '/config',
    component: <Home />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'Termos de Uso',
    path: '/terms-of-use',
    component: <TermsOfUsePage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: false,
    isAuthenticationPage: false,
  },
  {
    title: 'EDIT CHARGING PROFILE',
    path: '/charging_profile/edit/:id',
    component: <ChargingProfileEditPage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: false,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'CREATE CHARGING PROFILE',
    path: '/charging_profile/create',
    component: <ChargingProfileCreatePage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'Create Ocpp Tag',
    path: '/ocpptags/create',
    component: <OcppTagsCreatePage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'Update Ocpp Tag',
    path: '/ocpptags/edit/:id',
    component: <OcppTagEditPage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'Delete Ocpp Tag',
    path: '/ocpptags/delete/:id',
    component: <></>,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'Manage Unknown Ocpp Tags',
    path: '/ocpptags/unknown',
    component: <UnknownTagListPage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: false,
    isAuthenticationPage: false,
  },
  {
    title: 'TEST START TRANSACTION',
    path: '/qrcode/start/:chargeBoxId/:connectorId',
    component: <QrCodeStartPage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: false,
    isAuthenticationPage: false,
  },
  {
    title: 'Push Notification',
    path: '/push_notification',
    component: <PushNotificationPage />,
    isMenuSidebarItem: true,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: true,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
    menuIcon: (fill: string) => <PushNotificationIcon fill={fill} />,
    externalLink: true,
  },
  {
    title: 'Privacy Policy',
    path: '/privacy-policy',
    component: <PrivacyPolicyPage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: false,
    isAuthenticationPage: false,
  },
  {
    title: 'Rateio',
    path: 'https://datastudio.google.com/reporting/31a3c677-a7d3-4f5c-bbf3-10e26f42e343/page/IlroB?s=pDGvFVMESWE',
    component: <></>,
    isMenuSidebarItem: true,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: false,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'Not Found',
    path: '/not-found',
    component: <NotFoundPage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: false,
    isAuthenticationPage: false,
  },
  {
    title: 'SEARCH CPF TENANT PARENT',
    path: '/users_data/tenant_parent/cpf',
    component: <></>,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'Vouchers',
    path: '/vouchers',
    component: <VoucherListPage />,
    isMenuSidebarItem: true,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: true,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
    menuIcon: (fill: string) => <VoucherIcon fill={fill} />,
  },
  {
    title: 'Vouchers Create',
    path: '/vouchers/create',
    component: <VoucherCreatePage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'Vouchers Create Select',
    path: '/vouchers/create/select_users',
    component: <VoucherSelectUsersPage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'Vouchers Edit Select',
    path: '/vouchers/edit/:id/select_users',
    component: <VoucherSelectUsersPage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'Voucher Page',
    path: '/voucher/:id',
    component: <VoucherDetailPage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'Voucher Edit Page',
    path: '/voucher/:id/edit',
    component: <VoucherEditPage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'Pricing Profiles',
    path: '/dynamic-pricing',
    component: <ProfileListPage />,
    isMenuSidebarItem: true,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: true,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
    menuIcon: (fill: string) => (
      <DollarCircleOutlined color={fill} style={{ fontSize: 16 }} />
    ),
  },
  {
    title: 'Definir precificação',
    path: '/dynamic-pricing/:id',
    component: <ProfileCreatePage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'Dia da semana ou Exceção',
    path: '/dynamic-pricing/:id/:weekday',
    component: <SetPricingPage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'Advanced Settings',
    path: '/virtual-groups',
    component: <VirtualGroupListPage />,
    isMenuSidebarItem: true,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: true,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
    menuIcon: (fill: string) => (
      <SettingOutlined color={fill} style={{ fontSize: 16 }} />
    ),
  },
  {
    title: 'Advanced Settings Chargeboxes',
    path: '/virtual-groups/chargeboxes/select',
    component: <VirtualGroupChargeboxes />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'Advanced Settings',
    path: '/virtual-groups/view/:id',
    component: <VirtualGroupPage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },

  {
    title: 'Advanced Settings',
    path: '/virtual-groups/edit/:id',
    component: <VirtualGroupEditPage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'Advanced Settings Create',
    path: '/virtual-groups/create',
    component: <VirtualGroupCreatePage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    // MV2-2006: liberada para quem acessa o grupo (ver smartChargingSimulatorRoute no AppRouter)
    title: 'Simulador de Smart Charging',
    path: '/virtual-groups/smart-charging/simulator',
    component: <SmartChargingSimulatorPage />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'Advanced Settings Edit Chargeboxes',
    path: '/virtual-groups/edit/:id/chargeboxes',
    component: <VirtualGroupEditChargeboxes />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'Cancel Idle Payment',
    path: '/cancel-idle-payment',
    component: <div />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'Manual Actions',
    path: '/transactions/manual-actions',
    component: <div />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
  },
  {
    title: 'Autenticação 3D Secure',
    path: '/three-ds-authentication',
    component: <ThreeDSAuthentication />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: false,
    isAuthenticationPage: false,
  },
  {
    title: 'Monitoring',
    path: '/monitoring',
    component: <MonitoringPage />,
    isMenuSidebarItem: true,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: false,
    needAuthentication: true,
    isAuthenticationPage: false,
    menuIcon: (fill: string) => <ChartPieIcon fill={fill} />,
  },
  {
    title: 'OCPI',
    path: '/ocpi',
    component: <OcpiPage />,
    isMenuSidebarItem: true,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: true,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
    menuIcon: (fill: string) => (
      <ApiOutlined color={fill} style={{ fontSize: 16 }} />
    ),
  },
  {
    title: 'Maintenance',
    path: '/maintenance',
    component: <MaintenanceList />,
    isMenuSidebarItem: true,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: true,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
    menuIcon: (fill: string) => (
      <ToolOutlined color={fill} style={{ fontSize: 16 }} />
    ),
  },
  {
    title: 'Data and BI',
    path: '/analytics',
    component: <AnalyticsPage />,
    isMenuSidebarItem: true,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: true,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
    menuIcon: (fill: string) => (
      <LineChartOutlined color={fill} style={{ fontSize: 16 }} />
    ),
  },
  {
    title: 'Create Account',
    path: '/createaccount',
    component: <CreateAccount />,
    isMenuSidebarItem: false,
    isMenuHeaderItem: false,
    isMenuHeaderMobileItem: false,
    exact: true,
    needAuthentication: true,
    isAuthenticationPage: false,
    menuIcon: (fill: string) => (
      <UserAddOutlined color={fill} style={{ fontSize: 16 }}/>
    ),
  },
];
